/**
 * Space key guidance (RESTORE_PLAN R1.5): the pre-November ProjectKeyBestPractices help, opened
 * from "Key guidance" beside the Space Key field in CreateSpaceDialog and SpaceEditDialog.
 */
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';

interface SpaceKeyGuidanceProps {
  open: boolean;
  onClose: () => void;
}

export function SpaceKeyGuidance({ open, onClose }: SpaceKeyGuidanceProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Jira Space Key Best Practices</DialogTitle>
          <DialogDescription>
            Guidelines for choosing an effective Jira Space Key
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            <p>
              When deciding on the Space Key for a new Jira space, it's worth spending a little extra time up-front to avoid headaches later. Below are best practices, considerations and recommended guidelines.
            </p>

            <div className="space-y-4">
              <h3>Key Considerations</h3>

              <div className="space-y-3">
                <h4>Uniqueness & clarity</h4>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    Ensure each space key is <strong className="text-foreground">globally unique</strong> in your Jira instance. In particular, the key appears in every issue ID (e.g., ABC-123) so re-using or picking ambiguous keys causes confusion.
                  </li>
                  <li>
                    Use an abbreviation that <strong className="text-foreground">clearly identifies the space/team</strong> (rather than something generic or cryptic). As one blog says: "decide on a naming rule for your Spaces… both Space name and Space keys".
                  </li>
                  <li>
                    Keys must conform to Jira's format restrictions: <strong className="text-foreground">by default uppercase letters only, at least 2 characters</strong>.
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <h4>Future-proofing</h4>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    Think about growth: will the team expand, will the space morph into multiple sub-teams, will there be many related spaces? A key that is too tightly focused might become misleading.
                  </li>
                  <li>
                    <strong className="text-foreground">Beware of renaming later:</strong> Changing a space key is possible but has implications (re-indexing, link changes, attachments directory, board filters etc).
                  </li>
                  <li>
                    <strong className="text-foreground">Keep it short</strong> (often ≤ 5 characters) so issue IDs remain readable. The Tempo blog recommends "never to use more than 5 letters as a Space key".
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <h4>Consistency with naming conventions</h4>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    Align with your organization's naming scheme. For example: Dept-Product, Region-Team, BusinessArea, etc.
                  </li>
                  <li>
                    Use a key that integrates well with your naming for configuration items (workflows, screens, schemes) so you maintain clarity. For example: "Issue type scheme … MKT – Workflow Scheme" where "MKT" is the space key.
                  </li>
                  <li>
                    Avoid mixing formats. Decide early: maybe "ENG" for engineering, "MKT" for marketing, "OPS" for operations, etc.
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <h4>Avoid ambiguity or re-use of obsolete keys</h4>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    Don't pick a key that might be misunderstood or have multiple meanings.
                  </li>
                  <li>
                    Avoid re-using a key from a retired or archived space unless you're sure it's fully cleaned up and documented (so historical issue IDs don't confuse people).
                  </li>
                  <li>
                    Document the meaning of each key so future users understand what they refer to.
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <h4>Consider integrations, reporting, and external links</h4>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    The key is visible in issue URLs, filters, dashboards, Confluence links, integrations (Slack, email notifications, automation). A confusing key leaks into all these surfaces.
                  </li>
                  <li>
                    Some tools depend on issue keys (JQL filters, board filter queries) – if you later rename the key you'll need to update filters and links.
                  </li>
                  <li>
                    If you have external stakeholders (clients, vendors) referencing issues, use a key that is professional and meaningful to them as well.
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <h3>Recommended Guidelines / Best Practices Summary</h3>
              
              <ol className="list-decimal pl-6 space-y-3 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Use 2-5 uppercase letters for the key.</strong>
                </li>
                
                <li>
                  <strong className="text-foreground">Ensure the key:</strong>
                  <ul className="list-disc pl-6 mt-1 space-y-1">
                    <li>clearly identifies the space/team/business domain,</li>
                    <li>won't conflict with existing keys,</li>
                    <li>won't become misleading as the space grows.</li>
                  </ul>
                </li>
                
                <li>
                  <strong className="text-foreground">Decide on a naming convention and apply it consistently.</strong> For example:
                  <ul className="list-disc pl-6 mt-1 space-y-1">
                    <li>DEPT abbreviation (e.g., ENG, MKT, HR) or</li>
                    <li>Product abbreviation (e.g., PAY for Payments group) or</li>
                    <li>RegionTeam (e.g., USW for US West) if your org structure demands it.</li>
                  </ul>
                </li>
                
                <li>
                  <strong className="text-foreground">Avoid key changes later if possible</strong> — plan wisely. If change is needed, ensure you follow the documented process and know the impact (re-indexing, broken links/filters).
                </li>
                
                <li>
                  <strong className="text-foreground">Document the meaning of the key</strong> in your space setup page or admin handbook so future users/admins understand it.
                </li>
                
                <li>
                  <strong className="text-foreground">When in doubt, involve stakeholders</strong> (team leads, Jira admins) in approving new space keys to ensure alignment and avoid duplicate/ambiguous keys.
                </li>
              </ol>
            </div>

            <div className="text-muted-foreground border-t pt-4 mt-6">
              <p>
                <strong className="text-foreground">References:</strong> This guidance is compiled from Atlassian documentation, Tempo.io blog, and industry best practices for Jira configuration.
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}